import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ReviewCard } from "@/components/ReviewCard";
import { Stars } from "@/components/Stars";
import { useReviews } from "@/hooks/useReviews";

/** Home page strip with the three latest approved reviews. Renders nothing
 * when no review has been approved yet — we never show fabricated proof. */
export function ReviewsStrip() {
  const { items, total, averageRating } = useReviews(3);
  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-white border-t border-line" data-testid="reviews-strip">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
              Client Feedback
            </span>
            <h2 className="font-poster text-3xl md:text-4xl font-bold text-steel mt-2">
              What clients say
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <Stars rating={Math.round(averageRating)} />
            <span className="font-mono-sub text-xs text-mute" data-testid="reviews-strip-summary">
              {averageRating.toFixed(1)} · {total} review{total === 1 ? "" : "s"}
            </span>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((review, i) => (
            <Reveal key={review.id} delay={i * 60}>
              <ReviewCard review={review} compact />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <Link
            to="/reviews"
            data-testid="reviews-strip-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-royal-dark transition-colors"
          >
            Read all reviews or leave yours <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
